'use client'

import { useRef, useState, useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons'

import SearchResultItem, { TSearchResult, TItemDefaultClick } from './SearchResultItem'

import { useOnClickOutside } from '@/app/hooks/useOnClickOutside'
import useDebounce from '@/app/hooks/useDebounce'
import useShipSearch from '@/app/hooks/teqplay-api/useShipSearch'
import useSwitchBookmarks from '@/app/hooks/useSwitchBookmarks'
import { Spinner, Input, Button, useToast } from '@/app/components/teqplay-ui'
import { useClientApi } from '@/app/hooks/useClientApi'
import { useAnalytics } from '@/app/lib/analytics'
import { useRouter } from '@/i18n/navigation'
import { QUERY_PARAMS } from '@/app/constants'
import type { TLineUp } from '@/api/v1/resources/lineup'

import './SearchField.scss'

export type TSearchFieldProps = {
  // Lineup mode props
  lineupMode?: boolean
  lineup?: TLineUp | null
  onAddVesselToLineup?: (imo: string) => Promise<void>
  // Control open state from parent (for lineup mode)
  isOpenExternal?: boolean
  onClose?: () => void
  // Default click behavior for search result items
  itemDefaultClick?: TItemDefaultClick
}

export default function SearchField({
  lineupMode = false,
  lineup,
  onAddVesselToLineup,
  isOpenExternal,
  onClose,
  itemDefaultClick = 'GO_TO_VESSEL_PAGE'
}: TSearchFieldProps) {
  const t = useTranslations('SearchResultsSection')
  const [query, setQuery] = useState<string>('')
  const [isOpenInternal, setIsOpenInternal] = useState<boolean>(false)
  const [loadingImos, setLoadingImos] = useState<Set<string>>(new Set())
  const [addedImos, setAddedImos] = useState<Set<string>>(new Set())

  // Use external control if provided (lineup mode), otherwise use internal state
  const isOpen = isOpenExternal !== undefined ? isOpenExternal : isOpenInternal

  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { trackVesselSearched } = useAnalytics()

  // Set of IMOs already in the lineup
  const lineupImos = useMemo(() => {
    if (!lineup) return new Set<string>()
    return new Set(lineup.vessels.map((v) => v.imo))
  }, [lineup])

  const close = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      setIsOpenInternal(false)
    }
    setQuery('')
    setAddedImos(new Set())
    setLoadingImos(new Set())
  }, [onClose])

  const handleOpen = useCallback(() => {
    if (isOpenExternal === undefined) {
      setIsOpenInternal(true)
    }
  }, [isOpenExternal])

  useOnClickOutside(containerRef, close, searchInputRef)

  const debouncedQuery = useDebounce(query, 500)
  const { data: results, isLoading } = useShipSearch(debouncedQuery)

  const { addBookmarkNow, getIsVesselBookmarked } = useSwitchBookmarks()
  const { api } = useClientApi()
  const { toast } = useToast()
  const router = useRouter()

  const handleFollowClick = async (e: React.MouseEvent, result: TSearchResult) => {
    e.preventDefault()
    e.stopPropagation()

    if (!getIsVesselBookmarked(result.imo) && !loadingImos.has(result.imo)) {
      setLoadingImos((prev) => new Set([...prev, result.imo]))
      try {
        const vesselData = await api.vesselJourney.get({ imo: result.imo })
        addBookmarkNow(vesselData)
        close()
        router.push({
          pathname: `/vessels/${result.imo}`,
          query: { [QUERY_PARAMS.FOLLOWED]: 'true' }
        })
      } catch (error) {
        toast({
          title: t('followFailed'),
          variant: 'error'
        })
      } finally {
        setLoadingImos((prev) => {
          const newSet = new Set(prev)
          newSet.delete(result.imo)
          return newSet
        })
      }
    }
  }

  const handleAddToLineupClick = async (e: React.MouseEvent, result: TSearchResult) => {
    e.preventDefault()
    e.stopPropagation()

    if (!onAddVesselToLineup || !lineup) return

    if (lineupImos.has(result.imo) || addedImos.has(result.imo)) {
      toast({
        title: t('vesselAlreadyInLineup'),
        variant: 'warning'
      })
      return
    }

    if (!loadingImos.has(result.imo)) {
      setLoadingImos((prev) => new Set([...prev, result.imo]))
      try {
        await onAddVesselToLineup(result.imo)
        setAddedImos((prev) => new Set([...prev, result.imo]))
        toast({
          title: t('addToLineupSuccess'),
          variant: 'success'
        })
        // Keep modal open for multiple additions
      } catch (error) {
        toast({
          title: t('addToLineupFailed'),
          variant: 'error'
        })
      } finally {
        setLoadingImos((prev) => {
          const newSet = new Set(prev)
          newSet.delete(result.imo)
          return newSet
        })
      }
    }
  }

  // Check if a vessel is already in the lineup (either originally or added in this session)
  const isVesselInLineup = useCallback(
    (imo: string) => lineupImos.has(imo) || addedImos.has(imo),
    [lineupImos, addedImos]
  )

  // Only render the search field trigger if not in lineup mode (lineup mode is controlled externally)
  const renderSearchTrigger = !lineupMode && (
    <div className="search-field">
      {/* TODO: Create an Input component on teqplay ui that support icon */}
      <div className="field-input">
        <FontAwesomeIcon icon={faSearch} />
        <Input
          ref={searchInputRef}
          type="search"
          id="search-input"
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          onFocus={handleOpen}
        />
      </div>
    </div>
  )

  return (
    <>
      {renderSearchTrigger}

      {/* Search Modal */}
      {isOpen && (
        <div
          className="search-modal-overlay"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              close()
            }
          }}
        >
          <div ref={containerRef} className="search-modal" role="dialog" aria-modal="true">
            <div className="search-modal-header">
              <div className="search-modal-input">
                <FontAwesomeIcon icon={faSearch} />
                <Input
                  type="text"
                  className="modal-search-input"
                  name="modal-query"
                  placeholder={t('searchPlaceholder')}
                  aria-label={t('searchPlaceholder')}
                  pattern="[^/]+"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <Button
                className="close-button"
                variant="brand-secondary"
                onClick={close}
                aria-label="Close search"
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>

            <div className="search-modal-content">
              {isLoading ? (
                <div className="spinner-container">
                  <Spinner loading />
                </div>
              ) : debouncedQuery.trim() === '' ? (
                <div className="empty-state">
                  <p>{t('searchPlaceholder')}</p>
                </div>
              ) : results.length > 0 ? (
                <>
                  <div className="results-header">
                    <span className="subtext">
                      {t('numberOfVessel', { count: results.length })}
                    </span>
                  </div>
                  {results.length > 0 && (
                    <div className="results">
                      {results.map((result) => (
                        <SearchResultItem
                          key={result.imo}
                          result={result}
                          isBookmarked={getIsVesselBookmarked(result.imo)}
                          isLoading={loadingImos.has(result.imo)}
                          onLinkClick={() => {
                            if (!lineupMode) {
                              close()
                            }
                            trackVesselSearched({
                              imo: result.imo,
                              vesselName: result.name
                            })
                          }}
                          onFollowClick={handleFollowClick}
                          // Lineup mode props
                          lineupMode={lineupMode}
                          isInLineup={isVesselInLineup(result.imo)}
                          onAddToLineupClick={handleAddToLineupClick}
                          // Default click behavior
                          itemDefaultClick={itemDefaultClick}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  <p>{t('noVesselsFound', { query: debouncedQuery })}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
